import React, { Fragment } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  HTMLSelect,
  Icon,
} from '@blueprintjs/core';
import { RootState } from 'data/root.reducers';
import { getNavigatorLanguage, getNavigatorLanguages } from 'i18n/i18n.utils';
import i18n from 'i18n';
import { VIETNAMESE_LANGUAGES_CODES } from 'i18n/i18n.types';
import { LoginInterface } from 'data/authentication/authentication.types';
import { WithAuditDetails } from 'data/types';
import { AppToaster } from '../../shared/toaster';
import ss8logo from '../../assets/ss8-logo.png';
import { getDXSettingsAndReturn } from '../../xcipio/discovery-xcipio-actions';
import LoginMessage from './loginMessage';
import './login.scss';

const DEFAULT_PAGE = '/warrants';
const LOGIN_WARNING_STORAGE_KEY = 'showLoginActivityWarning';

class Login extends React.Component<any, any> {
  constructor(props: any) {
    super(props);

    const language = getNavigatorLanguage();
    i18n.changeLanguage(language);

    this.state = {
      login: '',
      password: '',
      language,
    };
  }

  checkAuthentication = () => {
    // handle existing auth token in local storage
    // should eventually have "isValid" check on server
    const { isAuthenticated } = this.props.authentication;
    const { token } = window.localStorage;
    if (isAuthenticated) {
      AppToaster.clear();
      this.props.history.push(DEFAULT_PAGE);
    } else if (token) {
      this.props.doCheckToken(token);
    }
  }

  componentDidMount() {
    this.checkAuthentication();
    if (this.props.location.password != null && this.props.location.user != null) {
      this.setState({
        login: this.props.location.user,
        password: this.props.location.password,
      });
    }
  }

  async componentDidUpdate(prevProps: any) {
    const {
      isAuthenticated,
      errorStatus,
      privileges,
    } = this.props.authentication;

    const oldIsAuthenticated = prevProps.authentication.isAuthenticated;
    if (!oldIsAuthenticated && isAuthenticated) {
      const dxSettings = await this.props.getDXSettingsAndReturn();
      if (dxSettings?.displayUserLoginWarning) {
        sessionStorage.setItem(LOGIN_WARNING_STORAGE_KEY, 'true');
      } else {
        sessionStorage.removeItem(LOGIN_WARNING_STORAGE_KEY);
      }
      AppToaster.clear();
      this.props.history.push(DEFAULT_PAGE);
    }

    if (oldIsAuthenticated !== isAuthenticated) this.checkAuthentication();
    if (errorStatus === '401' && prevProps.authentication.errorStatus !== errorStatus) {
      this.setState({ password: '' });
    } else if (errorStatus === '489' && prevProps.authentication.errorStatus !== errorStatus) {
      this.props.setPasswordUpdateFlag();
      this.props.clearLoginMessage();
      this.props.history.push({
        pathname: '/authentication/passwordupdate',
        username: this.state.login,
      });
    } else if ((errorStatus === '488' || errorStatus === '487') &&
      (prevProps.authentication.errorStatus === '' || prevProps.authentication.errorStatus === '489')) {
      this.props.doFetchLoginMessage();
    }

    if (isAuthenticated) {
      const {
        isExternalDashboardEnabled,
        externalDashboardIP,
        logiOAuthClientId,
        logiOAuthClientSecret,
        logiOAuthClientName,
        logiAdminAccount,
        logiAdminUserName,
        logiAdminPswd,
        logiSupervisorUserName,
        logiSupervisorPswd,
      } = await this.props.getDXSettingsAndReturn();

      if (isExternalDashboardEnabled && externalDashboardIP) {
        await this.props.authenticateLogiUser({
          user: this.state.login,
          password: this.state.password,
          privileges,
          externalDashboardIP,
          logiOAuthClientId,
          logiOAuthClientSecret,
          logiOAuthClientName,
          logiAdminAccount,
          logiAdminUserName,
          logiAdminPswd,
          logiSupervisorUserName,
          logiSupervisorPswd,
        });
      }
    }
  }

  handleLoginMessageClick = () => {
    const { doLogin, authentication: { errorStatus } } = this.props;
    const { login, password } = this.state;

    if (errorStatus === '488') {
      doLogin(this.getLoginFormData(
        login,
        password,
        'message'
      ));

    } else if (errorStatus === '487') {
      doLogin(this.getLoginFormData(
        login,
        password,
        'confirm'
      ));
    }
  }

  handleLoginMessageCancel = () => {
    this.props.clearErrorStatus();
    this.props.clearLoginMessage();
  }

  handleChange = (e: any) => {
    this.setState({ [e.target.name]: e.target.value });
  }

  handleSubmit = (e: any) => {
    e.preventDefault();

    const { login, password } = this.state;
    const { doLogin } = this.props;

    doLogin(this.getLoginFormData(
      login,
      password
    ));
  }

  getLoginFormData = (
    login: string,
    password: string,
    action?: string,
  ): WithAuditDetails<LoginInterface> => {

    const data = action != null ?
      { user: login, password, action } :
      { user: login, password };

    const networkBody: WithAuditDetails<LoginInterface> = { data };

    return networkBody;
  }

  onKeyDown = (e: any) => {
    if (e.key === 'Enter') this.handleSubmit(e);
  }

  handleLanguageChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(value);
    this.setState({
      language: value,
    });
  }

  getLanguageOptions() {
    const { t } = this.props;

    const languageOptions = [
      { value: 'en', label: t('login.language.english') },
    ];

    const navigatorLanguages = getNavigatorLanguages();
    const hasVietnamese = navigatorLanguages.some((language) => VIETNAMESE_LANGUAGES_CODES.includes(language));
    if (hasVietnamese) {
      languageOptions.push({ value: 'vi', label: t('login.language.vietnamese') });
    }

    return languageOptions;
  }

  shouldShowLanguageSelect() {
    const languageOptions = this.getLanguageOptions();
    // Only show the language select option if there is more than one language
    // to be displayed
    return languageOptions.length > 1;
  }

  render() {
    const {
      t,
      authentication: {
        errorStatus,
        errorMessage,
        loginMessage,
      },
    } = this.props;

    const {
      login,
      password,
      language,
    } = this.state;

    return (
      <div className="login-container">
        {(errorStatus === '488' || errorStatus === '487') ? (
          <LoginMessage
            loginMessage={loginMessage}
            {...this.props}
            handleClick={this.handleLoginMessageClick}
            handleCancel={this.handleLoginMessageCancel}
          />
        ) :
          (
            <Fragment>
              <div className="login-form">
                <div className="login-form-header">
                  <img className="login-logo" src={ss8logo} alt={t('login.ss8Login')} />
                  <h1>{t('login.xcipio')}</h1>
                </div>
                <form>
                  <div className="bp3-input-group">
                    <input
                      type="text"
                      name="login"
                      className="bp3-input"
                      placeholder={t('login.enterLogin')}
                      value={login}
                      onChange={this.handleChange}
                      onKeyDown={this.onKeyDown}
                    />

                  </div>
                  <div className="bp3-input-group">
                    <input
                      type="password"
                      name="password"
                      className="bp3-input"
                      placeholder={t('login.enterPassword')}
                      value={password}
                      onChange={this.handleChange}
                      onKeyDown={this.onKeyDown}
                    />
                  </div>
                  {this.shouldShowLanguageSelect() && (
                    <HTMLSelect
                      data-testid="language"
                      name="language"
                      options={this.getLanguageOptions()}
                      onChange={this.handleLanguageChange}
                      value={language}
                      fill
                    />
                  )}

                  {(errorStatus) && (
                    <div className="errorDisplay">
                      <Icon intent="danger" icon="error" iconSize={18} />&nbsp; &nbsp; <span>{errorMessage === 'Bad credentials' ? t('login.error.wrongPasswordOrUsername') : errorMessage}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      onClick={this.handleSubmit}
                      className="bp3-button bp3-large bp3-icon-submit"
                      value="submit"
                    >{t('login.button.submit')}
                    </button>
                  </div>
                </form>
              </div>
            </Fragment>
          )}
      </div>
    );
  }
}

const mapStateToProps = ({
  discoveryXcipio: {
    discoveryXcipioSettings: {
      externalDashboardIP,
      logiOAuthClientId,
      logiOAuthClientSecret,
      logiOAuthClientName,
      logiAdminAccount,
      logiAdminUserName,
      logiAdminPswd,
      logiSupervisorUserName,
      logiSupervisorPswd,
      displayUserLoginWarning,
    },
  },
}: RootState) => ({
  externalDashboardIP,
  logiOAuthClientId,
  logiOAuthClientSecret,
  logiOAuthClientName,
  logiAdminAccount,
  logiAdminUserName,
  logiAdminPswd,
  logiSupervisorUserName,
  logiSupervisorPswd,
  displayUserLoginWarning,
});

const mapDispatchToProps = {
  getDXSettingsAndReturn,
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(Login));
